import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check, CircleAlert, Mail, RefreshCw } from 'lucide-react'
import { Alert, Button } from '@agion/ui'
import { Field } from '@/components/common/Field'
import { GoogleIcon, MicrosoftIcon } from '@/components/common/ProviderIcons'
import { authService } from '@/services/auth'
import { authErrorMessage } from '@/services/auth-errors'
import { newPasswordHint } from '@/services/password-hint'
import { config } from '@/utils/config'
import { buildExternalOAuthLoginUrl } from '@/utils/auth-continuation'

interface InvitationInfo {
  valid: boolean
  email?: string
  organizationName?: string
  role?: string
  inviterName?: string
  expiresAt?: string
  requiresVerificationCode: boolean
  userExists?: boolean
  errorCode?: string
}

function IdPButtons({ token, dividerText }: { token: string; dividerText: string }) {
  const { t } = useTranslation('auth')
  const handleIdPLogin = (provider: 'google' | 'microsoft') => {
    sessionStorage.setItem('invitation_token', token)
    window.location.href = buildExternalOAuthLoginUrl(provider, config.authServiceUrl, {
      invitationToken: token,
    })
  }

  return (
    <>
      <div className="lp-divider">{dividerText}</div>
      <div className="lp-social-grid">
        <Button type="button" variant="outline" onClick={() => handleIdPLogin('google')} className="lp-btn-social">
          <GoogleIcon />
          {t('social.google')}
        </Button>
        <Button type="button" variant="outline" onClick={() => handleIdPLogin('microsoft')} className="lp-btn-social">
          <MicrosoftIcon />
          {t('social.microsoft')}
        </Button>
      </div>
    </>
  )
}

function ResendCodeLink({ token }: { token: string }) {
  const { t } = useTranslation('auth')
  const [cooldown, setCooldown] = useState(0)
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const handleResend = useCallback(async () => {
    if (cooldown > 0 || sending) return
    setSending(true)
    setMessage(null)
    try {
      await authService.resendVerificationCode(token)
      setMessage({ type: 'success', text: t('invite.resendSuccess') })
      setCooldown(60)
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : t('invite.resendError') })
    } finally {
      setSending(false)
    }
  }, [token, cooldown, sending, t])

  return (
    <div>
      {message && (
        <p className={`lp-field-msg ${message.type === 'success' ? 'lp-field-msg-success' : 'lp-field-msg-error'}`}>
          {message.type === 'success'
            ? <Check size={14} strokeWidth={2.5} aria-hidden="true" />
            : <CircleAlert size={14} strokeWidth={2.5} aria-hidden="true" />}
          {message.text}
        </p>
      )}
      <p className="lp-field-msg lp-field-msg-hint">
        {t('invite.didntReceiveCode')}{' '}
        {cooldown > 0 ? (
          <span>
            {t('invite.resendCooldown', { seconds: cooldown })}
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            onClick={handleResend}
            disabled={sending}
            className="lp-link-btn"
          >
            {sending && <RefreshCw className="animate-spin" size={12} aria-hidden="true" />}
            {t('invite.resendButton')}
          </Button>
        )}
      </p>
    </div>
  )
}

function InviteHeader({ info }: { info: InvitationInfo }) {
  const { t } = useTranslation('auth')
  const orgName = info.organizationName || 'Organization'
  const title = info.organizationName
    ? t('invite.title', { organization: orgName })
    : t('invite.titleDefault')

  return (
    <div className="lp-heading-block lp-rise lp-rise-2">
      <h2 className="lp-heading">{title}</h2>
      <p className="lp-subheading">
        {info.inviterName
          ? t('invite.subtitle', { inviter: info.inviterName, role: info.role || 'member' })
          : t('invite.subtitleDefault', { role: info.role || 'member' })}
      </p>
    </div>
  )
}

export function ExistingUserForm({ token, info }: { token: string; info: InvitationInfo }) {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('invite.validationCodeRequired'))
      return
    }

    setIsSubmitting(true)
    try {
      const result = await authService.registerWithInvitation({
        token,
        verificationCode,
        email: info.email || '',
        password: '',
        firstName: '',
        lastName: '',
      })
      if (result.success) {
        if (result.redirect_to) {
          window.location.href = result.redirect_to
        } else {
          navigate('/login')
        }
      }
    } catch (err) {
      setError(authErrorMessage(err, t, 'invite.acceptFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <InviteHeader info={info} />

      <form onSubmit={handleSubmit} className="lp-form lp-rise lp-rise-3">
        {error && (
          <Alert variant="error">
            <span>{error}</span>
          </Alert>
        )}

        <Field
          id="existing-email"
          type="email"
          label={t('invite.email')}
          value={info.email || ''}
          disabled
          locked
          leadingIcon={<Mail size={16} />}
        />

        <div>
          <div className="lp-code">
            <Field
              id="existing-verification-code"
              type="text"
              label={t('invite.verificationCode')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('invite.verificationCodePlaceholder')}
              maxLength={6}
              required
              hint={t('invite.verificationCodeHint')}
            />
          </div>
          <ResendCodeLink token={token} />
        </div>

        <Button type="submit" className="lp-btn-primary w-full" disabled={isSubmitting} isLoading={isSubmitting}>
          {isSubmitting ? t('invite.accepting') : t('invite.acceptButton')}
          {!isSubmitting && <ArrowRight size={16} aria-hidden="true" />}
        </Button>

        <IdPButtons token={token} dividerText={t('invite.orSignInWith')} />
      </form>

      <p className="lp-secondary-cta lp-rise lp-rise-4">
        {t('invite.wrongAccount')}{' '}
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="lp-link-btn"
        >
          {t('invite.switchAccount')}
        </Button>
      </p>
    </>
  )
}

export function NewUserForm({ token, info }: { token: string; info: InvitationInfo }) {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('invite.validationCodeRequired'))
      return
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('invite.validationNameRequired'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('invite.validationPasswordMatch'))
      return
    }

    setIsSubmitting(true)
    try {
      const result = await authService.registerWithInvitation({
        token,
        verificationCode,
        email: info.email || '',
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      })
      if (result.success) {
        if (result.redirect_to) {
          window.location.href = result.redirect_to
        } else {
          navigate('/login')
        }
      }
    } catch (err) {
      setError(authErrorMessage(err, t, 'invite.registrationFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <InviteHeader info={info} />

      <form onSubmit={handleSubmit} className="lp-form lp-rise lp-rise-3">
        {error && (
          <Alert variant="error">
            <span>{error}</span>
          </Alert>
        )}

        <Field
          id="new-email"
          type="email"
          label={t('invite.email')}
          value={info.email || ''}
          disabled
          locked
          leadingIcon={<Mail size={16} />}
        />

        <div>
          <div className="lp-code">
            <Field
              id="new-verification-code"
              type="text"
              label={t('invite.verificationCode')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('invite.verificationCodePlaceholder')}
              maxLength={6}
              required
              hint={t('invite.verificationCodeHint')}
            />
          </div>
          <ResendCodeLink token={token} />
        </div>

        <div className="lp-field-row">
          <Field
            id="first-name"
            type="text"
            label={t('invite.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('invite.firstNamePlaceholder')}
            required
          />
          <Field
            id="last-name"
            type="text"
            label={t('invite.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('invite.lastNamePlaceholder')}
            required
          />
        </div>

        <Field
          id="new-password"
          type={showPassword ? 'text' : 'password'}
          label={t('invite.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('invite.passwordPlaceholder')}
          hint={newPasswordHint(t)}
          required
          labelAction={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="lp-field-action"
              aria-controls="new-password confirm-password"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              {showPassword ? t('login.hidePassword') : t('login.showPassword')}
            </Button>
          }
        />

        <Field
          id="confirm-password"
          type={showPassword ? 'text' : 'password'}
          label={t('invite.confirmPassword')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t('invite.confirmPasswordPlaceholder')}
          required
        />

        <Button type="submit" className="lp-btn-primary w-full" disabled={isSubmitting} isLoading={isSubmitting}>
          {isSubmitting ? t('invite.creating') : t('invite.createButton')}
          {!isSubmitting && <ArrowRight size={16} aria-hidden="true" />}
        </Button>

        <IdPButtons token={token} dividerText={t('invite.orContinueWith')} />
      </form>

      <p className="lp-support">
        {t('invite.termsNotice')}{' '}
        <a href="https://agion.dev/terms">{t('invite.termsOfService')}</a>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        {' & '}
        <a href="https://agion.dev/privacy">{t('invite.privacyPolicy')}</a>
      </p>

      <p className="lp-secondary-cta lp-rise lp-rise-4">
        {t('invite.hasAccount')}{' '}
        <Button
          variant="ghost"
          onClick={() => {
            sessionStorage.setItem('invitation_token', token)
            navigate('/login')
          }}
          className="lp-link-btn"
        >
          {t('invite.signInInstead')}
        </Button>
      </p>
    </>
  )
}
